// Types
import { AiToolActionType, AiToolState, AiLocalAiConversation, AiToolAction } from './aiToolStateTypes';

// Utils
import {
    convertConversationMessagesToFollowupPromptShortcuts,
    convertConversationMessagesToResultObjects,
} from './aiToolUtils';

// Constants
import { DEFAULT_LOCAL_CONVERSATION_STATE, ROOT_FOLLOWUP_PROMPT_SHORTCUT } from './aiToolStateConstants';

const handleUpdateConversation = (
    state: AiToolState,
    { conversationId, updates }: { conversationId: string; updates: Partial<AiLocalAiConversation> },
): AiToolState => {
    const isFetchedAndHasNoFollowups =
        state.conversations[conversationId]?.fetched === true &&
        state.conversations[conversationId]?.selectedPromptShortcuts.length === 0;

    // always add the root followup prompt shortcut if the conversation is fetched and has no followups
    if (isFetchedAndHasNoFollowups) {
        updates.selectedPromptShortcuts = [ROOT_FOLLOWUP_PROMPT_SHORTCUT];
    }

    return {
        ...state,
        conversations: {
            ...state.conversations,
            [conversationId]: {
                ...state.conversations[conversationId],
                ...updates,
            },
        },
    };
};

const handleLoadConversation = (state: AiToolState, action: AiToolAction): AiToolState => {
    if (action.type !== AiToolActionType.LOAD_CONVERSATION) return state;

    const { conversationId, conversation } = action.payload;

    // convert the conversation messages into a list of results
    const results = convertConversationMessagesToResultObjects(action.payload.conversation.messages);

    // load the followup prompt shortcuts from the last assistant message
    const followups = convertConversationMessagesToFollowupPromptShortcuts(action.payload.conversation.messages);

    return {
        ...state,
        conversations: {
            ...state.conversations,
            [conversationId]: {
                ...DEFAULT_LOCAL_CONVERSATION_STATE,
                ...conversation,
                messages: [], // remove messages, as they're the raw data, and now we have results we can discard
                results,
                fetching: false,
                fetched: true,
                error: null,
                selectedPromptShortcuts: [
                    {
                        ...ROOT_FOLLOWUP_PROMPT_SHORTCUT,
                        children: [...ROOT_FOLLOWUP_PROMPT_SHORTCUT.children, ...followups],
                    },
                ],
            },
        },
    };
};

const handleLoadConversationStubs = (state: AiToolState, action: AiToolAction): AiToolState => {
    if (action.type !== AiToolActionType.LOAD_CONVERSATION_STUBS) return state;

    const newConversationStubAcc: AiToolState['conversations'] = {};

    const newConversationStubs = action.payload.conversations.reduce((acc, conversationStub) => {
        const conversationId = conversationStub._id || '';

        const loadedConversation: AiLocalAiConversation = {
            ...DEFAULT_LOCAL_CONVERSATION_STATE,
            ...conversationStub,
        };

        return {
            ...acc,
            [conversationId]: loadedConversation,
        };
    }, newConversationStubAcc);

    return {
        ...state,
        conversations: {
            ...state.conversations,
            ...newConversationStubs,
        },
    };
};

const handleCreateConversation = (state: AiToolState, action: AiToolAction): AiToolState => {
    if (action.type !== AiToolActionType.CREATE_CONVERSATION) return state;

    return {
        ...state,
        conversations: {
            [action.payload.conversationId]: {
                ...DEFAULT_LOCAL_CONVERSATION_STATE,
                _id: action.payload.conversationId,
                fetched: true,
                fetching: false,
                selectedPromptShortcuts: action.payload.rootPromptShortcut ? [action.payload.rootPromptShortcut] : [],
            },
            ...state.conversations,
        },
    };
};

const handleAddConversationResults = (state: AiToolState, action: AiToolAction): AiToolState => {
    if (action.type !== AiToolActionType.ADD_CONVERSATION_RESULTS) return state;

    return {
        ...state,
        conversations: {
            ...state.conversations,
            [action.payload.conversationId]: {
                ...state.conversations[action.payload.conversationId],
                results: [...state.conversations[action.payload.conversationId].results, ...action.payload.results],
            },
        },
    };
};

const handleSetConversationPromptShortcuts = (state: AiToolState, action: AiToolAction): AiToolState => {
    if (action.type !== AiToolActionType.SET_CONVERSATION_PROMPT_SHORTCUTS) return state;

    const existingRootFollowupShortcut = state.conversations[action.payload.conversationId].selectedPromptShortcuts[0];
    const rootFollowupShortcut = existingRootFollowupShortcut || ROOT_FOLLOWUP_PROMPT_SHORTCUT;

    return {
        ...state,
        conversations: {
            ...state.conversations,
            [action.payload.conversationId]: {
                ...state.conversations[action.payload.conversationId],
                selectedPromptShortcuts: [
                    {
                        ...rootFollowupShortcut,
                        children: action.payload.promptShortcuts,
                    },
                ],
            },
        },
    };
};

const handleResetConversationPromptShortcuts = (state: AiToolState, action: AiToolAction): AiToolState => {
    if (action.type !== AiToolActionType.RESET_CONVERSATION_PROMPT_SHORTCUTS) return state;

    return {
        ...state,
        conversations: {
            ...state.conversations,
            [action.payload.conversationId]: {
                ...DEFAULT_LOCAL_CONVERSATION_STATE,
                _id: action.payload.conversationId,
                fetched: true,
                fetching: false,
                selectedPromptShortcuts: action.payload.rootPromptShortcut ? [action.payload.rootPromptShortcut] : [],
            },
        },
    };
};

const aiToolStateReducer = (state: AiToolState, action: AiToolAction): AiToolState => {
    switch (action.type) {
        case AiToolActionType.SET_CURRENT_CONVERSATION_ID:
            return {
                ...state,
                currentConversationId: action.payload,
            };
        case AiToolActionType.SET_MODE:
            return {
                ...state,
                mode: action.payload,
            };
        case AiToolActionType.SET_RETURN_MODE:
            return {
                ...state,
                returnMode: action.payload,
            };
        case AiToolActionType.SET_TRANSITION_DIRECTION:
            return {
                ...state,
                transitionDirection: action.payload,
            };
        case AiToolActionType.UPDATE_CONVERSATION:
            return handleUpdateConversation(state, {
                conversationId: action.payload.conversationId,
                updates: action.payload.updates,
            });
        case AiToolActionType.LOAD_CONVERSATION:
            // convert messages into results
            return handleLoadConversation(state, action);
        case AiToolActionType.LOAD_CONVERSATION_STUBS:
            return handleLoadConversationStubs(state, action);
        case AiToolActionType.CREATE_CONVERSATION:
            return handleCreateConversation(state, action);
        case AiToolActionType.ADD_CONVERSATION_RESULTS:
            return handleAddConversationResults(state, action);
        case AiToolActionType.SET_CONVERSATION_PROMPT_SHORTCUTS:
            return handleSetConversationPromptShortcuts(state, action);
        case AiToolActionType.RESET_CONVERSATION_PROMPT_SHORTCUTS:
            return handleResetConversationPromptShortcuts(state, action);
        default:
            return state;
    }
};

export default aiToolStateReducer;
